import { useTranslation } from '@pypestream/translations';
import { useReportBugModal as useSharedReportBugModal } from '@pypestream/ui-patterns';
import {
  MAX_SCREENSHOTS,
  type ScreenshotRejectionReason,
} from '@pypestream/utils';
import { useMemo } from 'react';

import { useUserCtxSelector } from '../../../xstate/app.xstate';
import { screenshotRejectionKey } from './report-bug-modal.utils';

/**
 * Pro Studio's Report a bug form.
 *
 * Everything that is not app-specific lives in `@pypestream/ui-patterns`; what
 * stays here is exactly what differs between apps — this app's translation
 * scope, its XState hooks, and the name it reports under. The same shape
 * `ErrorBoundary` uses for `AppErrorBoundary`.
 */
export const useReportBugModal = ({ enabled }: { enabled?: boolean } = {}) => {
  const [t] = useTranslation();

  const reporter = useUserCtxSelector((ctx) => ({
    email: ctx.user?.email,
    name:
      ctx.user?.firstName && ctx.user?.lastName
        ? `${ctx.user.firstName} ${ctx.user.lastName}`
        : undefined,
  }));

  // Memoised to keep the prop's IDENTITY stable across renders. Not to prevent
  // a remount: React reconciles an element of the same type in the same
  // position by updating the existing instance, so the form's state survives a
  // new `labels` object either way.
  const labels = useMemo(
    () => ({
      title: String(t('pro-studio/navigation:report_bug_modal.title')),
      subtitle: String(t('pro-studio/navigation:report_bug_modal.subtitle')),
      messagePlaceholder: String(
        t('pro-studio/navigation:report_bug_modal.message_placeholder')
      ),
      screenshotsHint: String(
        t('pro-studio/navigation:report_bug_modal.screenshots_hint', {
          count: MAX_SCREENSHOTS,
        })
      ),
      attach: String(
        t('pro-studio/navigation:report_bug_modal.buttons.attach')
      ),
      cancel: String(
        t('pro-studio/navigation:report_bug_modal.buttons.cancel')
      ),
      submit: String(
        t('pro-studio/navigation:report_bug_modal.buttons.submit')
      ),
      sending: String(
        t('pro-studio/navigation:report_bug_modal.buttons.sending')
      ),
      done: String(t('pro-studio/navigation:report_bug_modal.buttons.done')),
      sendFailed: String(
        t('pro-studio/navigation:report_bug_modal.send_failed')
      ),
      successTitle: String(
        t('pro-studio/navigation:report_bug_modal.success.title')
      ),
      successBody: String(
        t('pro-studio/navigation:report_bug_modal.success.body')
      ),
      copied: String(
        t('pro-studio/navigation:report_bug_modal.success.copied')
      ),
      copyAria: (id: string) =>
        String(
          t('pro-studio/navigation:report_bug_modal.success.copy_aria', { id })
        ),
      screenshotAlt: (name: string) =>
        String(
          t('pro-studio/navigation:report_bug_modal.screenshot_alt', { name })
        ),
      removeScreenshot: (name: string) =>
        String(
          t('pro-studio/navigation:report_bug_modal.buttons.remove', { name })
        ),
      messageLabel: String(
        t('pro-studio/navigation:report_bug_modal.message_label')
      ),
      submitHint: String(
        t('pro-studio/navigation:report_bug_modal.submit_hint')
      ),
      copyFailed: String(
        t('pro-studio/navigation:report_bug_modal.copy_failed')
      ),
      screenshotAttached: (name: string) =>
        String(
          t('pro-studio/navigation:report_bug_modal.screenshot_attached', {
            name,
          })
        ),
      screenshotRemoved: (name: string) =>
        String(
          t('pro-studio/navigation:report_bug_modal.screenshot_removed', {
            name,
          })
        ),
      dropHint: String(t('pro-studio/navigation:report_bug_modal.drop_hint')),
      rejected: Object.fromEntries(
        (
          ['not-an-image', 'too-large', 'too-many', 'over-total-size'] as const
        ).map((reason) => [
          reason,
          (name: string) => String(t(screenshotRejectionKey(reason), { name })),
        ])
      ) as Record<ScreenshotRejectionReason, (name: string) => string>,
    }),
    [t]
  );

  return useSharedReportBugModal({
    app: 'pro-studio',
    reporter,
    labels,
    enabled,
  });
};
